import addInteractiveElementsForCompose from 'owa-link-preview/lib/utils/addInteractiveElementsForCompose';
import { BaseLinkPreviewObject } from 'owa-link-preview/lib/utils/BaseLinkPreviewObject';
import createLinkPreview from 'owa-link-preview/lib/utils/createLinkPreview';
import rehydrateLinkPreview from 'owa-link-preview/lib/utils/rehydrateLinkPreview';
import { PreviewSize, updatePreviewSize } from 'owa-link-preview/lib/utils/updatePreviewSize';
import { lazySubscribeToResizeEvent, lazyUnsubscribeFromResizeEvent } from 'owa-resize-event';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import removeComposeOnlyContent from '../utils/removeComposeOnlyContent';
import { Editor, EditorPlugin } from 'roosterjs-editor-core';
import {
    ChangeSource,
    PluginEvent,
    PluginEventType,
    ContentChangedEvent,
    ExtractContentEvent,
    ContentPosition,
    PluginDomEvent,
    BeforePasteEvent,
    PositionType,
} from 'roosterjs-editor-types';
import KeyboardCharCodes from 'owa-hotkeys/lib/utils/keyboardCharCodes';
import * as Constants from 'owa-link-preview/lib/utils/constants';

const EDITOR_WIDTH_COMPACT_IMAGE_SIZE_CUTOFF = 700;
const LINK_PREVIEW_DIV_SELECTOR = '[id^=LPBorder]';
const LINK_PREVIEW_CHANGE_SOURCE = 'LinkPreview';

export default class LinkPreviewPlugin implements EditorPlugin {
    private editor: Editor;
    private linkPreviewObjects: BaseLinkPreviewObject[];
    private previewSize: PreviewSize;
    private featureEnabled: boolean;
    private isDisposed: boolean; // plugin might be disposed of while waiting for awaiting async tasks

    constructor() {
        this.linkPreviewObjects = [];
        this.featureEnabled = getUserConfiguration().UserOptions.LinkPreviewEnabled;
    }

    public getName() {
        return 'LinkPreview';
    }

    public initialize(editor: Editor) {
        this.editor = editor;
        lazySubscribeToResizeEvent.importAndExecute(this.onResize);
        this.onResize();
    }

    public dispose() {
        this.linkPreviewObjects = null;
        this.editor = null;
        this.isDisposed = true;
        lazyUnsubscribeFromResizeEvent.importAndExecute(this.onResize);
    }

    public onPluginEvent(event: PluginEvent): void {
        if (!this.featureEnabled) {
            return;
        }

        if (event.eventType === PluginEventType.ContentChanged) {
            const contentChangedEvent: ContentChangedEvent = event as ContentChangedEvent;
            if (
                contentChangedEvent.source === ChangeSource.AutoLink &&
                contentChangedEvent.data &&
                contentChangedEvent.data instanceof HTMLAnchorElement
            ) {
                const anchor: HTMLAnchorElement = <HTMLAnchorElement>contentChangedEvent.data;
                this.generateLinkPreviewBelowAnchor(anchor);
            } else if (contentChangedEvent.source === ChangeSource.SetContent) {
                this.rehydrateLinkPreviewsInEditorBody();
            }
        } else if (event.eventType === PluginEventType.ExtractContent) {
            const extractContentEvent = event as ExtractContentEvent;
            extractContentEvent.content = removeComposeOnlyContent(extractContentEvent.content);
        } else if (event.eventType === PluginEventType.KeyDown) {
            const domEvent = event as PluginDomEvent;
            const keyboardEvent: KeyboardEvent = domEvent.rawEvent as KeyboardEvent;
            this.onKeyDownEvent(domEvent, keyboardEvent);
        } else if (event.eventType === PluginEventType.BeforePaste) {
            // VSO 27302: URLs that have already been hyperlinked, i.e. ones copied from mail,
            // should still generate link previews on paste
            const beforePasteEvent = event as BeforePasteEvent;
            const fragment = beforePasteEvent.fragment;

            // VSO 27255: Link previews are not generated when hyperlinks are copied and pasted from
            // mail in the editor, but are generated when the message is sent and read in the reading pane
            // Generate a link preview if the fragment contains just the anchor, since it could
            // already contain a link preview, and this covers the above case
            if (fragment?.childNodes && fragment.childNodes.length == 1) {
                const child = fragment.firstElementChild;
                if (child instanceof HTMLAnchorElement) {
                    const anchor: HTMLAnchorElement = <HTMLAnchorElement>child;
                    this.generateLinkPreviewBelowAnchor(anchor);
                }
            }
        } else if (event.eventType === PluginEventType.EditorReady) {
            this.rehydrateLinkPreviewsInEditorBody();
        }
    }

    private static onLinkPreviewRemoved(plugin: LinkPreviewPlugin) {
        return function(linkPreviewObject: BaseLinkPreviewObject) {
            if (
                plugin.editor &&
                linkPreviewObject &&
                linkPreviewObject.border &&
                linkPreviewObject.border.parentElement
            ) {
                // Move caret where link preview currently is (below hyperlink)
                plugin.moveCursorBelowElement(linkPreviewObject.border);

                // Delete link preview and remove from array of link preview objects
                plugin.editor.deleteNode(linkPreviewObject.border);
                plugin.linkPreviewObjects = plugin.linkPreviewObjects.filter(
                    (value: BaseLinkPreviewObject) => {
                        return value != linkPreviewObject;
                    }
                );
            }
        };
    }

    private isImageLink(anchor: HTMLAnchorElement): boolean {
        const path: string = anchor.href;
        return !!path && !!path.match('.(bmp|gif|jpe?g|png)$');
    }

    private onImageLoadedIntoEditor(onLoadEvent: Event) {
        // We loaded the image successfully, so unbind event handlers
        const imageElement: HTMLImageElement = <HTMLImageElement>onLoadEvent.target;
        if (imageElement) {
            imageElement.onload = null;
            imageElement.onerror = null;
        }
    }

    private onImageError(onErrorEvent: Event) {
        // We failed to load the image, so unbind event handlers and remove the image element from the message
        const imageElement: HTMLImageElement = <HTMLImageElement>onErrorEvent.target;

        if (imageElement) {
            imageElement.onload = null;
            imageElement.onerror = null;

            if (imageElement.parentNode != null) {
                imageElement.parentNode.removeChild(imageElement);
            }
        }
    }

    private generateLinkPreviewBelowAnchor(anchor: HTMLAnchorElement) {
        if (this.isImageLink(anchor)) {
            this.tryInsertImageIntoEditor(anchor);
        } else {
            createLinkPreview(anchor, true /*inCompose*/).then(linkPreviewObject => {
                if (linkPreviewObject && !this.isDisposed) {
                    this.linkPreviewObjects.push(linkPreviewObject);
                    updatePreviewSize(linkPreviewObject, this.previewSize);
                    this.insertLinkPreview(anchor, linkPreviewObject.border);

                    const callback = LinkPreviewPlugin.onLinkPreviewRemoved(this);
                    addInteractiveElementsForCompose(linkPreviewObject, callback);
                }
            });
        }
    }

    private insertLinkPreview(anchor: HTMLAnchorElement, linkPreview: HTMLDivElement) {
        // It's possible that the plugin has been disposed by the time this callback is executed, so make sure we still have a
        // valid reference to the editor before continuing
        if (!this.editor) {
            return;
        }

        this.insertLinkPreviewElementIntoEditor(linkPreview, anchor);

        // For Firefox (and perhaps other browsers), add an extra line break AFTER the LinkPreview element
        // Otherwise the user may not be able to easily place the cursor after the LinkPreview table
        if (linkPreview.parentNode) {
            linkPreview.parentNode.insertBefore(
                this.editor.getDocument().createElement('br'),
                linkPreview.nextSibling
            );
        }
    }

    private tryInsertImageIntoEditor(anchor: HTMLAnchorElement) {
        // It's possible that the plugin has been disposed by the time this callback is executed, so make sure we still have a
        // valid reference to the editor before continuing
        if (!this.editor) {
            return;
        }

        // Create the image element and try adding it to the editor
        const imageElement: HTMLImageElement = this.editor.getDocument().createElement('img');

        imageElement.onload = this.onImageLoadedIntoEditor;
        imageElement.onerror = this.onImageError;

        imageElement.style.maxWidth = '100%';
        imageElement.src = anchor.href;

        this.insertLinkPreviewElementIntoEditor(imageElement, anchor);
    }

    private insertLinkPreviewElementIntoEditor(element: HTMLElement, anchor: HTMLAnchorElement) {
        const placeholderSpan = this.editor.getDocument().createElement('span');
        this.editor.insertNode(placeholderSpan, {
            insertOnNewLine: false,
            position: ContentPosition.SelectionStart,
            replaceSelection: false,
            updateCursor: true,
        });

        // Select the anchor and use it as the reference point for inserting the link preview
        this.moveCursorBelowElement(anchor);

        this.editor.insertNode(element, {
            insertOnNewLine: true,
            position: ContentPosition.SelectionStart,
            replaceSelection: false,
            updateCursor: true,
        });

        // Now, select the placeholder span, use it to update the current editor selection and then delete it
        this.moveCursorBelowElement(placeholderSpan);
        this.editor.deleteNode(placeholderSpan);

        this.editor.triggerContentChangedEvent(LINK_PREVIEW_CHANGE_SOURCE);
        this.editor.addUndoSnapshot();
    }

    private rehydrateLinkPreviewsInEditorBody() {
        this.editor.queryElements(LINK_PREVIEW_DIV_SELECTOR, matchedElement => {
            rehydrateLinkPreview(matchedElement as HTMLDivElement).then(linkPreviewObject => {
                if (!this.isDisposed) {
                    this.linkPreviewObjects.push(linkPreviewObject);
                    addInteractiveElementsForCompose(
                        linkPreviewObject,
                        LinkPreviewPlugin.onLinkPreviewRemoved(this)
                    );
                }
            });
        });
    }

    private onResize = () => {
        // resize event can be triggered at the same time when the plugin is disposed due to resizing after we remove event listener,
        // so we need to add a check here
        const scrollContainer = this.editor && this.editor.getScrollContainer();
        if (scrollContainer) {
            if (scrollContainer.clientWidth < EDITOR_WIDTH_COMPACT_IMAGE_SIZE_CUTOFF) {
                this.setPreviewSize(PreviewSize.Compact);
            } else {
                this.setPreviewSize(PreviewSize.Standard);
            }
        }
    };

    private setPreviewSize(newPreviewSize: PreviewSize) {
        if (newPreviewSize != this.previewSize) {
            this.previewSize = newPreviewSize;

            // Update all existing previews:
            if (this.linkPreviewObjects) {
                this.linkPreviewObjects.forEach(lp => {
                    updatePreviewSize(lp, this.previewSize);
                });
            }
        }
    }

    private moveCursorBelowElement(element: HTMLElement) {
        this.editor.select(element, PositionType.After);
    }

    private onKeyDownEvent(event: PluginDomEvent, keyboardEvent: KeyboardEvent) {
        if (
            this.linkPreviewObjects &&
            this.linkPreviewObjects.length > 0 &&
            (keyboardEvent.which == KeyboardCharCodes.Backspace ||
                keyboardEvent.which == KeyboardCharCodes.Delete)
        ) {
            const contentTraverser = this.editor.getBlockTraverser(ContentPosition.SelectionStart);
            if (!contentTraverser) {
                return;
            }

            const currentElement =
                contentTraverser.currentBlockElement &&
                (contentTraverser.currentBlockElement.getEndNode() as HTMLElement);

            if (currentElement) {
                let linkPreview = null;

                const linkPreviewAboveElement = this.findLinkPreviewAboveElement(currentElement);
                if (linkPreviewAboveElement) {
                    // If the cursor is within the link preview, then the current node will be a child of the link preview
                    linkPreview = linkPreviewAboveElement;
                } else if (keyboardEvent.which == KeyboardCharCodes.Backspace) {
                    const previousInlineElement = contentTraverser.getPreviousInlineElement();
                    if (!previousInlineElement) {
                        // If the cursor is just after the link preview, then the previous block node itself will be the link preview
                        linkPreview = this.findLinkPreviewBeforeOrAfterElement(
                            currentElement,
                            true /* before */
                        );
                    }
                } else if (keyboardEvent.which == KeyboardCharCodes.Delete) {
                    const nextInlineElement = contentTraverser.getNextInlineElement();
                    if (!nextInlineElement) {
                        // If the cursor is just before the link preview, then the next block node itself will be the link preview
                        linkPreview = this.findLinkPreviewBeforeOrAfterElement(
                            currentElement,
                            false /* before */
                        );
                    }
                }

                if (linkPreview) {
                    this.editor.deleteNode(linkPreview);
                    this.preventDefaultEventAndStopPropagation(event);
                    this.editor.addUndoSnapshot();
                }
            }
        }
    }

    private findLinkPreviewAboveElement(element: HTMLElement) {
        const cursorWithinLinkPreview = Constants.elementHasLinkPreviewPrefix(
            element,
            Constants.LINK_PREVIEW_PREFIX
        );

        if (cursorWithinLinkPreview) {
            const linkPreviewBorderId = Constants.createLinkPreviewId(
                element.id,
                Constants.LINK_PREVIEW_BORDER_ID_BASE
            );
            const linkPreviewBorder = this.editor.queryElements('.' + linkPreviewBorderId);
            return linkPreviewBorder ? linkPreviewBorder[0] : null;
        }

        return null;
    }

    private findLinkPreviewBeforeOrAfterElement(element: HTMLElement, before: boolean) {
        const elementToCheck = (before
            ? element.previousSibling
            : element.nextSibling) as HTMLElement;
        const cursorBeforeOrAfterLinkPreview = Constants.elementHasLinkPreviewPrefix(
            elementToCheck,
            Constants.LINK_PREVIEW_BORDER_ID_BASE
        );
        return cursorBeforeOrAfterLinkPreview ? elementToCheck : null;
    }

    private preventDefaultEventAndStopPropagation(event: PluginDomEvent) {
        event.rawEvent.preventDefault();
        event.rawEvent.stopPropagation();
    }
}
